import { useAuthState } from '@/app/providers/auth'
import { useLocale } from '@/components/Link'
import { localeDate } from '@/components/page-specific/catering/common/helpers/date'
import useAllergenInfoText from '@/components/page-specific/catering/common/helpers/useAllergenInfoText'
import { useDeliveryTimeLabel } from '@/components/page-specific/catering/common/helpers/useDeliveryTimeLabel'
import { validateAll } from '@/components/page-specific/catering/common/helpers/validation'
import { AppErrors } from '@/components/page-specific/catering/common/types/AppErrors'
import { type UserBasketSettingsLocationFragment } from '@/generated/graphql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'
import { mobileMediaQuery } from '@caterdesk/ui--theme'
import {
  AccessTime,
  CalendarToday,
  Close,
  Edit,
  InfoRounded,
  LocationOn,
  Person,
} from '@mui/icons-material'
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogContent,
  GridLegacy as Grid,
  IconButton,
  InputAdornment,
  Tooltip as MuiTooltip,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import { setDay } from 'date-fns'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMedia } from 'react-use'

import {
  UserBasketSettings,
  validateUserBasketSettings,
  ValidUserBasketSettings,
} from '../../../common/domain/user-basket-settings'
import { useAddressInRangeCheck } from '../../hooks/useAddressInRangeCheck'
import { useVendorLocationData } from '../../store/vendorLocationStore'
import AddressLookup from './address-lookup'
import { DatePickerInput as DatepickerInput } from './datepicker-input'
import HeadCountInput from './headcount-input'
import TimeSelect from './time-select'
import { useAddressLabel } from './useAddressLabel'

type InputBarProps = {
  vendorId?: string
  modalOpen: boolean
  orderSelectedSettings: {
    date?: number
    time?: string
    headCount?: number
    location?: UserBasketSettingsLocationFragment
    weekdays?: Array<number>
  }
  hideFields?: {
    date?: boolean
  }
  disabled: boolean
  modalIsLoading: boolean
  showBasketEmbeddedVersion?: boolean
  loading?: boolean
  onValueChange: (values: UserBasketSettings) => void
  setModalOpen: (open: boolean) => void
  onConfirm: (settings: ValidUserBasketSettings) => void
}

type DraftUserSettings = Pick<UserBasketSettings, 'location' | 'date' | 'time' | 'headCount'>

const InputAddressBar = ({
  vendorId,
  orderSelectedSettings,
  modalOpen,
  modalIsLoading,
  disabled,
  hideFields,
  showBasketEmbeddedVersion,
  loading,
  onValueChange,
  setModalOpen,
  onConfirm,
}: InputBarProps) => {
  const tenant = useTenant()
  const { t } = useTranslation()
  const allergenInfoText = useAllergenInfoText(t)
  const locale = useLocale()

  const isMobileScreen = useMedia(mobileMediaQuery, false)
  const [closeCalendar, setCloseCalendar] = useState(false)

  const authState = useAuthState()

  const [draft, setDraft] = useState<DraftUserSettings>({
    location: orderSelectedSettings.location,
    date: orderSelectedSettings.date,
    time: orderSelectedSettings.time,
    headCount: orderSelectedSettings.headCount,
  })

  const {
    location: draftLocation,
    date: draftDate,
    time: draftTime,
    headCount: draftHeadCount,
  } = draft

  const { addressLabel: pinAddress, showFullAddress } = useAddressLabel(
    orderSelectedSettings.location,
  )

  const { validateLocationInRange, loadingInRange, inRangeError } = useAddressInRangeCheck({
    vendorId,
  })

  const [errors, setErrors] = useState<AppErrors>({})

  const deliveryTimeLabel = useDeliveryTimeLabel(orderSelectedSettings.time) || ''
  const hasBlockingAddressError = Boolean(errors.address)

  // Feature flag drives whether delivery times are enforced in validation
  const useDeliveryTimes = useFeatureFlag(SPLITS.GM_CUTOFF_5PM)
  const { availableVendorLocations, inRangeVendorLocations } = useVendorLocationData({
    useDeliveryTimes,
  })

  const draftState = useMemo(
    () => ({
      ...orderSelectedSettings,
      date: draftDate,
      location: draftLocation,
      headCount: draftHeadCount,
      time: draftTime,
    }),
    [orderSelectedSettings, draftDate, draftLocation, draftHeadCount, draftTime],
  )

  const draftValidationResult = useMemo(() => validateUserBasketSettings(draftState), [draftState])

  // Single, centralized validation runner
  const runValidation = useCallback(
    (userState: UserBasketSettings) => {
      const { newErrors } = validateAll({
        inRangeVendorLocations: availableVendorLocations,
        userState,
        useDeliveryTimes,
        deliveryTimes: tenant.features?.gmDeliveryTimeslots,
        t,
      })
      setErrors(newErrors)
      return newErrors
    },
    [availableVendorLocations, tenant.features?.gmDeliveryTimeslots, t, useDeliveryTimes],
  )

  const runValidationRef = useRef(runValidation)
  useEffect(() => {
    runValidationRef.current = runValidation
  }, [runValidation])

  // Initial + whenever key inputs change
  useEffect(() => {
    if (!modalOpen) return
    runValidationRef.current(draftState)
  }, [draftState, modalOpen])

  const orderCanBeFulfilled =
    draftValidationResult.type === 'success' && Object.values(errors).every((value) => !value)

  // Request guard to avoid out-of-order overwrites
  const latestRequestIdRef = useRef(0)
  const modalOpenRef = useRef(false)

  useEffect(() => {
    if (modalOpen && !modalOpenRef.current) {
      setDraft({
        location: orderSelectedSettings.location,
        date: orderSelectedSettings.date,
        time: orderSelectedSettings.time,
        headCount: orderSelectedSettings.headCount,
      })
      setErrors({})
      setCloseCalendar(false)
    }

    modalOpenRef.current = modalOpen
  }, [
    modalOpen,
    orderSelectedSettings.date,
    orderSelectedSettings.headCount,
    orderSelectedSettings.location,
    orderSelectedSettings.time,
  ])

  const checkInRangeLocations = useCallback(
    async (locationToCheck: UserBasketSettingsLocationFragment) => {
      const reqId = ++latestRequestIdRef.current
      const result = await validateLocationInRange(locationToCheck)

      if (reqId !== latestRequestIdRef.current) return { ok: false as const }

      if (!result.ok) {
        if (inRangeError) {
          setErrors((prev) => ({
            ...prev,
            address: t('marketplace:addressCheckFailed') || 'Address check failed',
          }))
        }
        return { ok: false as const }
      }

      return { ok: true as const }
    },
    [inRangeError, t, validateLocationInRange],
  )

  // Preload in-range on initial prop location
  // useEffect(() => {
  //   const location = orderSelectedSettings.location

  //   if (!location) {
  //     preloadLocationKeyRef.current = null
  //     return
  //   }

  //   const { latitude, longitude, country } = basketLocationToAddress(location)
  //   const formatCoordinate = (value?: number | string | null) => {
  //     if (typeof value === 'number' && Number.isFinite(value)) return value.toFixed(6)
  //     if (typeof value === 'string') {
  //       const parsed = Number.parseFloat(value)
  //       return Number.isFinite(parsed) ? parsed.toFixed(6) : value
  //     }
  //     return ''
  //   }

  //   const candidateKeyParts = [
  //     location.__typename ?? 'unknown',
  //     'id' in location ? ((location as { id?: string | null }).id ?? '') : '',
  //     formatCoordinate(latitude),
  //     formatCoordinate(longitude),
  //     country ?? '',
  //   ]
  //   const nextKey = candidateKeyParts.join('|')

  //   if (preloadLocationKeyRef.current === nextKey) return

  //   preloadLocationKeyRef.current = nextKey
  //   void checkInRangeLocations(location)
  // }, [orderSelectedSettings.location, checkInRangeLocations])

  const handleAddressChange = useCallback(
    async (newLocation: UserBasketSettingsLocationFragment) => {
      const result = await checkInRangeLocations(newLocation)

      if (result.ok) {
        setDraft((prev) => ({ ...prev, location: newLocation }))
        setErrors((prev) => ({ ...prev, address: '' }))
      } else {
        setErrors((prev) => ({
          ...prev,
          address: inRangeError
            ? t('marketplace:addressCheckFailed') || 'Address check failed'
            : 'Address is not in delivery range',
        }))
      }
    },
    [checkInRangeLocations, inRangeError, t],
  )

  const handleDateChange = useCallback((newDate: number) => {
    setDraft((prev) => ({ ...prev, date: newDate }))
  }, [])

  const handleTimeChange = useCallback((newTime: string) => {
    setDraft((prev) => ({ ...prev, time: newTime }))
  }, [])

  const handleHeadCountChange = useCallback((newHeadcount: number) => {
    setDraft((prev) => ({ ...prev, headCount: newHeadcount }))
  }, [])

  const handleConfirmModal = useCallback(() => {
    const nextState = draftState
    const validationErrors = runValidation(nextState)
    const hasErrors = Object.values(validationErrors).some(Boolean)
    const validationResult = validateUserBasketSettings(nextState)

    if (hasErrors || validationResult.type !== 'success') {
      return
    }

    onValueChange({
      location: nextState.location,
      date: nextState.date,
      time: nextState.time,
      headCount: nextState.headCount,
    })
    onConfirm(validationResult.value)
  }, [draftState, onConfirm, onValueChange, runValidation])

  const openModal = useCallback(() => {
    if (disabled) return
    setModalOpen(true)
  }, [disabled, setModalOpen])

  const closeModal = useCallback(() => {
    setModalOpen(false)
  }, [setModalOpen])

  const handleDialogClose = useCallback(
    (_event?: unknown, reason?: string) => {
      if (hasBlockingAddressError) return
      if (reason === 'backdropClick' && !orderCanBeFulfilled) return
      closeModal()
    },
    [closeModal, hasBlockingAddressError, orderCanBeFulfilled],
  )

  return (
    <>
      {showBasketEmbeddedVersion ? (
        <Box>
          <ButtonBase
            disabled={disabled}
            onClick={openModal}
            sx={{
              borderRadius: 2,
              bgcolor: 'background.paper',
              borderColor: 'action.disabled',
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              ':hover': { borderColor: 'action.active' },
            }}>
            <Grid container>
              <Grid
                item
                xs={12}
                padding={1}
                sx={{
                  borderWidth: '1px',
                  borderColor: 'action.disabled',
                  borderBottomStyle: 'solid',
                }}>
                <Stack width="100%" direction="row" justifyContent="space-between" spacing={1}>
                  <Stack direction="row" spacing={1} width="100%" alignItems="center">
                    <LocationOn fontSize="small" color="primary" />
                    {!loading ? (
                      <Typography
                        textAlign="left"
                        variant="body3"
                        color={pinAddress ? 'text.main' : 'text.disabled'}
                        sx={{ fontWeight: 500, flexGrow: 1 }}>
                        {pinAddress || t('marketplace:enterAnAddress')}
                      </Typography>
                    ) : (
                      <Skeleton sx={{ width: '100%' }} />
                    )}
                  </Stack>
                  {!disabled && !loading ? (
                    <Edit fontSize="small" color="primary" data-cy="Edit-location" />
                  ) : null}
                </Stack>
              </Grid>

              {orderSelectedSettings.weekdays?.length ? (
                <Grid
                  item
                  padding={1}
                  xs={12}
                  sx={{
                    borderWidth: '1px',
                    borderColor: 'action.disabled',
                    borderBottomStyle: 'solid',
                  }}>
                  <Stack direction="row" spacing={0.5}>
                    {orderSelectedSettings.weekdays.map((weekday) => (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        label={localeDate(setDay(new Date(), weekday), 'EEEEEE', locale)}
                        key={weekday}
                      />
                    ))}
                  </Stack>
                </Grid>
              ) : null}

              {!hideFields?.date ? (
                <Grid
                  item
                  padding={1}
                  xs={12}
                  sx={{
                    borderWidth: '1px',
                    borderColor: 'action.disabled',
                    borderBottomStyle: 'solid',
                  }}>
                  <Stack width="100%" direction="row" spacing={1} alignItems="center">
                    <CalendarToday fontSize="small" color="primary" />
                    {!loading ? (
                      <Typography
                        variant="body3"
                        color={orderSelectedSettings.date ? 'text.main' : 'text.disabled'}
                        sx={{ fontWeight: 500 }}>
                        {orderSelectedSettings.date
                          ? localeDate(orderSelectedSettings.date, 'do MMM yyyy', locale)
                          : t('marketplace:deliveryDate')}
                      </Typography>
                    ) : (
                      <Skeleton width={150} />
                    )}
                  </Stack>
                </Grid>
              ) : null}

              <Grid
                item
                padding={1}
                xs={8}
                sx={{
                  borderWidth: '1px',
                  borderColor: 'action.disabled',
                  borderRightStyle: 'solid',
                }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AccessTime fontSize="small" color="primary" />
                  {!loading ? (
                    <Typography
                      variant="body3"
                      color={deliveryTimeLabel ? 'text.main' : 'text.disabled'}
                      sx={{ fontWeight: 500 }}>
                      {deliveryTimeLabel || t('marketplace:time')}
                    </Typography>
                  ) : (
                    <Skeleton width={110} />
                  )}
                </Stack>
              </Grid>

              <Grid padding={1} item xs={4}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Person fontSize="small" color="primary" />
                  {!loading ? (
                    <Typography
                      variant="body3"
                      color={pinAddress ? 'text.main' : 'text.disabled'}
                      sx={{ fontWeight: 500 }}>
                      {orderSelectedSettings.headCount ?? 0}
                    </Typography>
                  ) : (
                    <Skeleton width={30} />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </ButtonBase>
        </Box>
      ) : (
        <Stack
          direction="row"
          useFlexGap
          flexWrap="wrap"
          spacing={1}
          alignItems="center"
          id="input-bar">
          <Typography width={{ xs: '100%', lg: 'fit-content' }} color="text.secondary">
            {t('marketplace:orderingFor')}
          </Typography>

          {orderSelectedSettings.weekdays?.length ? (
            <Stack direction="row" spacing={0.5}>
              {orderSelectedSettings.weekdays.map((weekday) => (
                <Chip
                  variant="outlined"
                  color="primary"
                  label={localeDate(setDay(new Date(), weekday), 'EEEEEE', locale)}
                  key={weekday}
                />
              ))}
            </Stack>
          ) : null}

          <TextField
            sx={{
              width: {
                xs: '100%',
                xl: showFullAddress ? '30%' : '200px',
                md: showFullAddress ? '28%' : '200px',
                sm: showFullAddress ? '23%' : '170px',
              },
              flexGrow: { xs: 1, md: 'unset' },
            }}
            onClick={openModal}
            size="small"
            placeholder={t('marketplace:deliverTo')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn color="primary" />
                  </InputAdornment>
                ),
                readOnly: true,
              },
            }}
            disabled={disabled}
            value={pinAddress || ''}
          />

          {!isMobileScreen ? (
            <>
              {!hideFields?.date ? (
                <TextField
                  sx={{ width: { xs: '150px', md: '170px' }, flexGrow: { xs: 1, md: 'unset' } }}
                  onClick={openModal}
                  size="small"
                  placeholder={t('marketplace:date')}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarToday color="primary" />
                        </InputAdornment>
                      ),
                      readOnly: true,
                    },
                  }}
                  disabled={disabled}
                  value={
                    orderSelectedSettings.date
                      ? localeDate(orderSelectedSettings.date, 'dd/MM/yyyy', locale)
                      : ''
                  }
                />
              ) : null}

              <TextField
                sx={{ width: '175px' }}
                onClick={openModal}
                size="small"
                placeholder={t('marketplace:time')}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTime color="primary" />
                      </InputAdornment>
                    ),
                    readOnly: true,
                  },
                }}
                disabled={disabled}
                value={deliveryTimeLabel || ''}
              />

              <TextField
                sx={{ width: '110px' }}
                onClick={openModal}
                size="small"
                placeholder="0"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="primary" />
                      </InputAdornment>
                    ),
                    readOnly: true,
                  },
                }}
                disabled={disabled}
                value={orderSelectedSettings.headCount?.toString() ?? '0'}
              />

              <Stack
                flexWrap="wrap"
                flexGrow={1}
                direction="row"
                alignItems="center"
                justifyContent="flex-end">
                {tenant.features?.cateringAllergenTooltip?.visibility !== false && (
                  <MuiTooltip title={allergenInfoText}>
                    <Button size="small" variant="text" startIcon={<InfoRounded />}>
                      {t('marketplace:allergens')}
                    </Button>
                  </MuiTooltip>
                )}
              </Stack>
            </>
          ) : null}
        </Stack>
      )}

      <Dialog
        open={modalOpen}
        fullWidth
        onClose={handleDialogClose}
        fullScreen={isMobileScreen}
        maxWidth="md"
        disableEscapeKeyDown={hasBlockingAddressError}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: isMobileScreen ? 0 : 4,
            maxWidth: isMobileScreen ? '100%' : '640px',
            width: isMobileScreen ? '100%' : '640px',
          },
        }}>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Stack direction="row" spacing={2}>
            <Box flexGrow={1} pb={2}>
              <Typography textAlign="left" variant="h5" component="span">
                {t('marketplace:confirmYourDetails')}
              </Typography>
            </Box>

            {isMobileScreen && (
              <Box>
                <IconButton onClick={handleDialogClose} disabled={hasBlockingAddressError}>
                  <Close />
                </IconButton>
              </Box>
            )}
          </Stack>

          <AddressLookup
            error={loadingInRange ? '' : errors.address}
            onAddressChanged={handleAddressChange}
            selectedLocation={draftLocation}
            availableAccountLocations={
              authState.type === 'authenticated' ? (authState.user.account?.locations ?? []) : []
            }
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <DatepickerInput
                error={errors.date}
                showErrors={Boolean(errors.date && !errors.headCount)}
                disabled={!draftLocation}
                dateValue={draftDate}
                onChange={handleDateChange}
                forceClose={closeCalendar}
                showChevron
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TimeSelect
                error={errors.time}
                onTimeChange={handleTimeChange}
                selectedDate={draftDate}
                selectedTime={draftTime}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <HeadCountInput
                onChange={handleHeadCountChange}
                headCount={draftHeadCount}
                error={draftTime && draftDate ? errors.headCount : ''}
                maxHeadCount={
                  availableVendorLocations.reduce(
                    (acc, loc) => Math.max(loc.details?.gmMaxOrder || 0, acc),
                    0,
                  ) || Number.POSITIVE_INFINITY
                }
                unusable={!draftTime}
                disabled={!draftTime}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              width: '100%',
              mt: 1,
              '@media (max-width: 768px)': {
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                borderTop: '1px solid',
                borderColor: 'divider',
                p: '10px',
                backgroundColor: 'background.paper',
              },
            }}>
            <Button
              variant="contained"
              disabled={!orderCanBeFulfilled}
              loading={modalIsLoading}
              onClick={handleConfirmModal}
              sx={{ px: 4, ml: 'auto', display: 'block' }}>
              {t('marketplace:confirm')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default InputAddressBar
